import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StatusBadge } from '../components/ui/status-badge';
import { StatsCard } from '../components/ui/stats-card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Percent,
  DollarSign,
  Users,
  Calendar,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DiscountAPI, Discount, DiscountPaginatedResponse } from '../lib/api';
import { toast } from '../components/ui/use-toast';
import { Pagination } from '../components/ui/pagination';
import DiscountFormModal from '../components/marketing/DiscountFormModal';
import NotificationManagement from '../components/marketing/NotificationManagement';

// Lazy load the promotion pages
const BannersPageContent = lazy(() => import('./BannersPage'));
const OffersPageContent = lazy(() => import('./OffersPage'));
const FeaturedDealsPageContent = lazy(() => import('./FeaturedDealsPage'));

interface DiscountStats {
  totalDiscounts: number;
  activeDiscounts: number;
  expiredDiscounts: number;
  totalUsage: number;
}

export default function MarketingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [discountStats, setDiscountStats] = useState<DiscountStats>({
    totalDiscounts: 0,
    activeDiscounts: 0,
    expiredDiscounts: 0,
    totalUsage: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Get active tab from URL or default to discounts
  const activeTab = searchParams.get('tab') || 'discounts';

  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'expired' | 'upcoming'>('all');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<Discount | null>(null);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        sort: 'createdAt',
        order: 'desc' as const
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await DiscountAPI.getAll(params);

      if (response.success) {
        setDiscounts(response.data.discounts);
        setPagination(response.data.pagination);

        // Calculate stats from the loaded discounts
        const stats = response.data.discounts.reduce((acc, discount) => {
          acc.totalDiscounts++;
          acc.totalUsage += discount.totalUses;

          if (discount.isActive && discount.isCurrentlyValid) {
            acc.activeDiscounts++;
          } else if (new Date(discount.validTo) < new Date()) {
            acc.expiredDiscounts++;
          }

          return acc;
        }, {
          totalDiscounts: 0,
          activeDiscounts: 0,
          expiredDiscounts: 0,
          totalUsage: 0
        });

        setDiscountStats(stats);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch discounts",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
      toast({
        title: "Error",
        description: "Failed to fetch discounts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscount = () => {
    setEditingDiscount(null);
    setIsCreateModalOpen(true);
  };

  const handleEditDiscount = (discount: Discount) => {
    setEditingDiscount(discount);
    setIsCreateModalOpen(true);
  };

  const handleDeleteDiscount = (discount: Discount) => {
    setDiscountToDelete(discount);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDiscount = async () => {
    if (!discountToDelete) return;

    try {
      const response = await DiscountAPI.delete(discountToDelete._id);

      if (response.success) {
        toast({
          title: "Success",
          description: "Discount deleted successfully"
        });

        fetchDiscounts();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete discount",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast({
        title: "Error",
        description: "Failed to delete discount",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setDiscountToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setEditingDiscount(null);
    fetchDiscounts();
  };

  const getDiscountStatusBadge = (discount: Discount) => {
    const now = new Date();
    const validFrom = new Date(discount.validFrom);
    const validTo = new Date(discount.validTo);

    if (!discount.isActive) {
      return <StatusBadge status="inactive" />;
    }

    if (now < validFrom) {
      return <Badge variant="outline">Upcoming</Badge>;
    }

    if (now > validTo) {
      return <Badge variant="destructive">Expired</Badge>;
    }

    if (discount.usageLimit && discount.totalUses >= discount.usageLimit) {
      return <Badge variant="destructive">Limit Reached</Badge>;
    }

    return <StatusBadge status="active" />;
  };

  const formatDiscountValue = (discount: Discount) => {
    if (discount.discountType === 'percentage') {
      return `${discount.value}%`;
    } else if (discount.discountType === 'fixed') {
      return `₹${discount.value}`;
    } else {
      return 'Free Shipping';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchDiscounts();
  }, [pagination.page, pagination.limit, searchQuery, statusFilter]);

  const StatsCardSkeleton = () => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between space-y-0">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
        <Skeleton className="h-8 w-16 mt-2" />
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold">Marketing & Promotions</h1>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={(value) => setSearchParams({ tab: value })}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="discounts">Discount Codes</TabsTrigger>
          <TabsTrigger value="banners">Banners</TabsTrigger>
          <TabsTrigger value="offers">Offers</TabsTrigger>
          <TabsTrigger value="deals">Featured Deals</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="discounts" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <>
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
              </>
            ) : (
              <>
                <StatsCard
                  title="Total Discounts"
                  value={discountStats.totalDiscounts.toString()}
                  icon={<Percent className="h-4 w-4 text-blue-600" />}
                />
                <StatsCard
                  title="Active"
                  value={discountStats.activeDiscounts.toString()}
                  icon={<DollarSign className="h-4 w-4 text-green-600" />}
                />
                <StatsCard
                  title="Expired"
                  value={discountStats.expiredDiscounts.toString()}
                  icon={<Calendar className="h-4 w-4 text-red-600" />}
                />
                <StatsCard
                  title="Total Usage"
                  value={discountStats.totalUsage.toString()}
                  icon={<Users className="h-4 w-4 text-purple-600" />}
                />
              </>
            )}
          </div>

          {/* Filters and Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex gap-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search discount codes..."
                  className="pl-8 w-full sm:w-[300px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm w-full sm:w-auto"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </div>

            <Button onClick={handleCreateDiscount}>
              <Plus className="h-4 w-4 mr-2" />
              Create Discount
            </Button>
          </div>

          {/* Discounts Table */}
          <Card>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Valid Period</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                    </TableRow>
                  ))
                ) : (discounts && discounts.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No discount codes found
                    </TableCell>
                  </TableRow>
                ) : (
                  discounts.map((discount) => (
                    <TableRow key={discount._id}>
                      <TableCell className="font-medium">{discount.code}</TableCell>
                      <TableCell>{discount.description}</TableCell>
                      <TableCell className="capitalize">{discount.discountType}</TableCell>
                      <TableCell>{formatDiscountValue(discount)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatDate(discount.validFrom)}</div>
                          <div className="text-muted-foreground">to {formatDate(discount.validTo)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{discount.totalUses}</div>
                          {discount.usageLimit && (
                            <div className="text-muted-foreground">/ {discount.usageLimit}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getDiscountStatusBadge(discount)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditDiscount(discount)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDiscount(discount)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </Card>

          {/* Pagination */}
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        </TabsContent>

        <TabsContent value="banners" className="space-y-6">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          }>
            <BannersPageContent />
          </Suspense>
        </TabsContent>

        <TabsContent value="offers" className="space-y-6">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          }>
            <OffersPageContent />
          </Suspense>
        </TabsContent>

        <TabsContent value="deals" className="space-y-6">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          }>
            <FeaturedDealsPageContent />
          </Suspense>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <NotificationManagement />
        </TabsContent>
      </Tabs>

      {/* Discount Form Modal */}
      <DiscountFormModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingDiscount(null);
        }}
        onSuccess={handleFormSuccess}
        discount={editingDiscount}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the discount code "{discountToDelete?.code}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDiscount}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 